'use client';

import { useEffect, useRef, type CSSProperties } from 'react';
import { createWheelStepper } from './reelGestures';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from './icons';

/**
 * Screen-edge chrome + gestures for the fullscreen shorts reel: a close
 * button in the top-right corner of the SCREEN (not the frame), and the
 * Instagram-style ↑/↓ chevron pair on the right edge. While mounted it
 * also owns the reel gestures — wheel down / swipe up / ArrowDown mean
 * "next short", the opposites mean "previous". Which short that IS is
 * not decided here: the popup asks its {@code ShortsSequencer}.
 */
export function ShortsFeedNav({
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
}: {
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('site-builder-blocks');

  // Latest handlers in refs so the window listeners bind once.
  const stateRef = useRef({ hasPrev, hasNext, onPrev, onNext });
  stateRef.current = { hasPrev, hasNext, onPrev, onNext };

  useEffect(() => {
    // One gesture = one step. Trackpads emit a burst of wheel events per
    // flick, so after a trigger the accumulator is cleared and further
    // deltas are swallowed for a cooldown window.
    const SWIPE_THRESHOLD = 60;
    const stepper = createWheelStepper();
    let touchStartY: number | null = null;

    const step = (dir: 1 | -1) => {
      const s = stateRef.current;
      if (dir === 1 && s.hasNext) s.onNext();
      else if (dir === -1 && s.hasPrev) s.onPrev();
    };

    // An embedded player (YouTube) grabs keyboard focus the moment it
    // autoplays, and from then on ArrowUp / ArrowDown go to the iframe,
    // not to this window. Hand focus back whenever an iframe takes it so
    // the reel keeps answering the keys (and the wheel overlays keep
    // their pointer target).
    const reclaimFocus = () => {
      window.setTimeout(() => {
        const el = document.activeElement;
        if (el && el.tagName === 'IFRAME') (el as HTMLElement).blur();
      }, 0);
    };

    // Diagnostics: `localStorage.microReelDebug = '1'` logs every wheel
    // event the reel sees and what it decided — the fastest way to tell
    // "the event never arrived" from "it arrived and was ignored".
    const debug =
      typeof localStorage !== 'undefined' && localStorage.getItem('microReelDebug') === '1';
    const onWheel = (e: WheelEvent) => {
      // The body is scroll-locked while the popup is open, so the wheel
      // has no default job left — it drives the reel. See reelGestures.ts
      // for why one flick is exactly one step.
      e.preventDefault();
      const dir = stepper.onWheel(e.deltaY, Date.now());
      if (debug) {
        const s = stateRef.current;
        const target = e.target as Element | null;
        console.debug('[reel] wheel', {
          deltaY: Math.round(e.deltaY),
          target: target?.tagName + (target?.className ? '.' + String(target.className) : ''),
          decided: dir,
          hasPrev: s.hasPrev,
          hasNext: s.hasNext,
          activeElement: document.activeElement?.tagName,
        });
      }
      if (dir !== 0) step(dir);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        step(1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        step(-1);
      }
    };

    const onTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0]?.clientY ?? null;
    };
    const onTouchEnd = (e: TouchEvent) => {
      if (touchStartY == null) return;
      const endY = e.changedTouches[0]?.clientY;
      if (endY == null) return;
      const delta = endY - touchStartY;
      touchStartY = null;
      // Finger moved up → the feed advances (same as scrolling down).
      if (delta < -SWIPE_THRESHOLD) step(1);
      else if (delta > SWIPE_THRESHOLD) step(-1);
    };

    // Capture phase, so nothing between the target and the window can
    // stop the reel from seeing a gesture.
    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('blur', reclaimFocus);
    // Belt and braces: an embedded player can (re)take focus without a
    // window blur we get to see — while the reel is open, keep checking.
    const focusPoll = window.setInterval(reclaimFocus, 400);
    reclaimFocus();
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.clearInterval(focusPoll);
      window.removeEventListener('wheel', onWheel, true);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('blur', reclaimFocus);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={onClose}
        aria-label={t('microChrome.close')}
        style={{ ...screenButton, top: 16, right: 16 }}
      >
        <CloseIcon />
      </button>

      {/* No neighbours at all (a lone short, or one playing as a path
          step) → no arrows: an all-disabled stack would only promise
          navigation that doesn't exist. */}
      {(hasPrev || hasNext) && (
        <div
          style={{
            position: 'fixed',
            right: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            zIndex: 3,
          }}
        >
          <button
            type="button"
            onClick={onPrev}
            disabled={!hasPrev}
            aria-label={t('microShorts.previous')}
            style={{ ...screenButton, position: 'static', opacity: hasPrev ? 1 : 0.35 }}
          >
            <ChevronUpIcon />
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={!hasNext}
            aria-label={t('microShorts.next')}
            style={{ ...screenButton, position: 'static', opacity: hasNext ? 1 : 0.35 }}
          >
            <ChevronDownIcon />
          </button>
        </div>
      )}
    </div>
  );
}

const screenButton: CSSProperties = {
  position: 'fixed',
  width: 44,
  height: 44,
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(255,255,255,0.14)',
  color: '#fff',
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  zIndex: 3,
};

function ChevronUpIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

function ChevronDownIcon() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}
